/**
 * 본인인증 앞에서 받는 동의.
 *
 * ## 왜 한 곳에 모으나
 *
 * 링크와 문구가 **아직 확정 전**이다(BTS 전용 개인정보 동의서를 만드는 중).
 * 화면 여기저기에 흩어 두면 바뀔 때 한 곳을 빠뜨리고, 그러면 같은 동의를 두
 * 화면이 다르게 설명하게 된다.
 *
 * ## 동의가 왜 필요한가
 *
 * 본인확인기관(KCB)으로 보내는 요청에 `agree1~4` 가 **전부 `Y` 여야** 한다.
 * 그 값은 «동의를 받았다» 는 선언이므로, 실제로 받지 않고 보내면 선언이
 * 거짓이 된다. 그래서 화면에서 받는다.
 *
 *     agree1  개인정보 수집/이용/취급위탁
 *     agree2  고유식별정보 처리
 *     agree3  본인확인서비스 이용약관
 *     agree4  통신사 이용약관
 *
 * 화면에서는 이 넷을 **두 묶음**으로 보여 준다. 네 줄을 그대로 늘어놓으면 읽지
 * 않고 «전체 동의» 만 누르게 되는데, 그러면 동의를 받는 의미가 없다.
 */
import BtsPrivacyTerms from "./BtsPrivacyTerms";

export type TermsItem = {
  key: string;
  /** 체크박스 옆에 보이는 이름. */
  title: string;
  /** 필수인가. 필수를 다 켜야 인증번호를 받을 수 있다. */
  required: boolean;
  /**
   * 전문. 둘 중 하나다.
   *
   * · `body` — **우리 문서**다. 화면에서 직접 그린다.
   * · `url` — **남의 문서**다. iframe 으로 띄운다. 우리가 고칠 수 없으므로
   *   내용을 옮겨 적지 않는다(옮겨 적으면 상대가 고쳤을 때 조용히 어긋난다).
   */
  url?: string;
  body?: React.ComponentType;
  /** 무엇에 동의하는지 한 줄. 제목만으로는 안 읽힌다. */
  summary: string;
  /**
   * 전문 페이지가 **좁은 고정 폭**인가.
   *
   * KCB 약관 페이지는 폭 380px 고정이라(실측), 넓은 모달에 넣으면 왼쪽에 붙고
   * 오른쪽이 통째로 빈다 — 덜 만든 화면처럼 보인다. 모달을 문서에 맞춘다.
   */
  narrow?: boolean;
};

export const IDENTITY_TERMS: TermsItem[] = [
  {
    key: "privacy",
    title: "개인정보 수집 및 이용 동의서",
    required: true,
    // **BTS 전용이다.** 앱·CRM 이 쓰는 `terms.frism.io` 동의서를 쓰지 않는 이유는
    // 그것이 **투자자문 상담** 기준이라서다 — 연소득·계좌정보·금융자산·재무상태·
    // 투자목적이 적혀 있는데 BTS 는 그중 무엇도 받지 않는다. 받지 않는 것에
    // 동의를 받으면 동의가 아니다.
    //
    // ※ 법무 검토 전 초안이다. 근거: us-api `docs/bts-개인정보-동의서-초안.md`
    body: BtsPrivacyTerms,
    summary:
      "이름·이메일로 계정을 식별합니다. 휴대폰번호·주민등록번호는 저장하지 않습니다.",
  },
  {
    key: "identity",
    title: "휴대폰 본인확인 서비스",
    required: true,
    url: "https://safe.ok-name.co.kr/eterms/agreement_all.jsp",
    narrow: true,
    summary:
      "본인확인기관(KCB)에 이름·생년월일·휴대폰번호를 전달해 본인 여부만 확인합니다.",
  },
];

/** 필수 항목을 모두 켰는가. */
export const allRequiredAgreed = (agreed: Record<string, boolean>): boolean =>
  IDENTITY_TERMS.filter((item) => item.required).every((item) => agreed[item.key]);
